import React, {useEffect, useMemo, useRef, useState} from "react";
import Heading from "../../components/Heading";
import ConfiguratorIcon from "../../components/ConfiguratorButton/ConfiguratorIcon";
import cx from "classnames";
import Icon from "../../components/Icon";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import Sidebar from "../Individualization/Progress/DefineAreas/Sidebar";
import styles from "./CustomizerPopup.module.css"
import { useTranslation } from "react-i18next";
import TextElementInput from "./TextElementInput";
import ImgElementInput from "./ImgElementInput";
import CuttingMaskElementInput from "./CuttingMaskElementInput";
import Checkout from "./Checkout";
import MediaOverview from "./MediaOverview";
import Loader from "../../components/Loader";
import {useCustomizer} from "../../contexts/CustomizerContext";
import AutoTextElementInput from "./AutoTextElementInput";
import {getTranslatedObject} from "../../helpers/getTranslatedObject";
import {StoredSpot, StoredTemplate, Template} from "../../types/Individualization";
import {useGlobalContext} from "../../contexts/globalContext";

const CustomizerPopup: React.FC = () => {
    const { t } = useTranslation(['customizer'])
    const [, setShowMediaOverview] = useState(false);
    const { plugin } = useGlobalContext()
    const {
        isLoading,
        canvas,
        resize,
        activeTemplate,
        activeTemplateId,
        setActiveTemplateId,
        individualization,
        uuids,
        downloadCustomization,
        buttonSetting
    } = useCustomizer()
    const wrapper = useRef<HTMLDivElement | null>(null)
    const canvasDiv = useRef<HTMLDivElement | null>(null)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)
    const [showSidebar, setShowSidebar] = useState<boolean>(true)

    useEffect(() => {
        if (!isLoading && wrapper.current && canvasDiv.current && !resizeObserver.current) {
            const s = (wrapper.current.getBoundingClientRect().width < canvasDiv.current.getBoundingClientRect().width) ? wrapper.current.getBoundingClientRect().width : canvasDiv.current.getBoundingClientRect().width
            canvas.setWidth(s).setHeight(s)
            canvasDiv.current.style.width = s+'px'
            canvasDiv.current.style.height = s+'px'

            canvasDiv.current?.append(canvas.getElement())
            canvasDiv.current?.append(canvas.getSelectionElement())

            resizeObserver.current = new ResizeObserver(() => {
                clearTimeout(timer.current)
                timer.current = setTimeout(() => {
                    if (canvasDiv.current && wrapper.current) {
                        const w = (wrapper.current.getBoundingClientRect().width < canvasDiv.current.getBoundingClientRect().width) ? wrapper.current.getBoundingClientRect().width : canvasDiv.current.getBoundingClientRect().width
                        resize(w)
                        canvasDiv.current.style.width = w+'px'
                        canvasDiv.current.style.height = w+'px'
                    }
                }, 500)
            });

            resizeObserver.current.observe(wrapper.current)
        }
    }, [isLoading, canvas, resize])

    const toggleMediaOverview = () => {
        setShowMediaOverview(prev => !prev);
    };

    const translatedTemplates = useMemo(() => {
        if (!plugin?.language) return individualization.templates
        return individualization.templates.map(t => getTranslatedObject<Template|StoredTemplate>(t, plugin?.language))
    }, [individualization.templates, plugin])

    const translatedSpots = useMemo(() => {
        if (!plugin?.language) return activeTemplate?.spots
        return activeTemplate?.spots.map(t => getTranslatedObject<StoredSpot>(t, plugin?.language))
    }, [activeTemplate?.spots, plugin])

    return isLoading ? (
        <div className={'c-body-wrapper d-flex justify-content-center align-items-center h-100'}>
            <Loader isLoading={isLoading} size={'l'}/>
        </div>
    ):(
        <>
            <MediaOverview />
            <div className={cx(styles['c-wrapper'])}>
                <div className={cx(styles['c-header-wrapper'])}>
                    <ConfiguratorIcon fill={buttonSetting?.background_color ?? '#525252'} />
                    <div className={cx(styles['c-header-title'])}>
                        <Heading level={3}>{t('header')}</Heading>
                    </div>
                </div>

                <div className={cx(styles['c-body-wrapper'])}>

                    <div ref={wrapper} className={cx(styles['c-canvas-wrapper'])}>
                        {individualization.templates.length > 1 &&
                            <Sidebar
                                templates={translatedTemplates}
                                active={activeTemplateId}
                                changeTemplate={(id) => setActiveTemplateId(Number(id))}
                                show={showSidebar}
                                toggleSidebar={() => setShowSidebar(prev => !prev)}
                                colors={{background: buttonSetting?.background_color ?? '#FC0', text: buttonSetting?.text_color ?? '#000'}}
                            />
                        }
                        <div onClick={() => downloadCustomization()} className={cx(styles['c-save-wrapper'])} style={{background: buttonSetting?.background_color ?? '#000'}}>
                            <Icon icon={faSave} color={buttonSetting?.text_color ?? '#2d2d2d'} size={'lg'} />
                        </div>
                        <div ref={canvasDiv} className={cx(styles['c-canvas'])}>

                        </div>
                    </div>
                    <div className={cx(styles['c-right-wrapper'])}>
                        <div className={cx(styles['c-input-wrapper'])}>
                            <Heading level={3} >
                                <span dangerouslySetInnerHTML={{ __html: individualization?.article?.cName }} />
                            </Heading>
                            {!isLoading && translatedSpots?.map((spot) => {
                                if (spot.type === 'text' && spot.sync_id === 0) {
                                    return <TextElementInput
                                        key={spot.id}
                                        spot={spot}
                                        uid={(uuids.get(spot.id) as string)}
                                    />
                                } else if (spot.type === 'auto text' && spot.sync_id === 0) {
                                    return <AutoTextElementInput
                                        key={spot.id}
                                        spot={spot}
                                        uid={(uuids.get(spot.id) as string)}
                                    />
                                }
                                else if (spot.type === 'image') {
                                    return <>
                                        <ImgElementInput
                                            key={spot.id}
                                            spot={spot}
                                            uid={(uuids.get(spot.id) as string)}
                                            toggleMediaOverview={toggleMediaOverview}
                                        />
                                    </>
                                } else if (spot.type === 'cutting mask') {
                                    return <CuttingMaskElementInput
                                        key={spot.id}
                                        spot={spot}
                                    />
                                } else return null
                            })}
                        </div>
                        <Checkout />
                    </div>
                </div>
            </div>
        </>
    )
}

export default CustomizerPopup
